import { action, set } from '@ember/object';
import { callbackError } from 'client-portal/utils/error-handling';
import { environmentRelationships } from 'client-portal/authenticators/environment';
import { htmlSafe } from '@ember/template';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { pluralize } from 'ember-inflector';
import { service } from '@ember/service';
import ENV from 'client-portal/config/environment';
import FastbootHelpersMixin from 'client-portal/mixins/route/fastboot-helpers';
import Route from '@ember/routing/route';
import classic from 'ember-classic-decorator';
import getCanonicalUrl from 'client-portal/utils/get-canonical-url';
import queryString from 'query-string';
import uuidv4 from 'uuid/v4';

@classic
export default class ApplicationRoute extends Route.extend(FastbootHelpersMixin) {
  @service mixpanel;
  @service store;
  @service currentPractice;
  @service session;
  @service request;
  @service analytics;
  @service stripe;
  @service keepalive;
  @service clientAppBanner;
  @service clientTimezone;
  @service widgetToken;
  @service intl;
  @service localeManager;
  @service fastboot;
  @service headData;
  @service router;
  @service prescreener;

  queryParams = {
    universalLink: { refreshModel: false },
  };

  init() {
    super.init(...arguments);
    if (!this.isFastBoot) {
      this.keepalive.start();
    }
    this.#addCanonicalLinkListener();
  }

  model() {
    let environment = this.hasShoebox && this.store.peekAll('environment').firstObject;
    if (!environment) {
      let models = [
        'environment',
        'practice',
        'office',
        'phone',
        'practice-website',
        'client',
        'client-access',
        'treatable-client',
      ];

      let relationships = [
        'currentPractice',
        'currentClientAccess',
        'currentClient',
        'currentClientOptions',
      ];

      let fields = models.reduce((acc, modelName) => {
        let model = this.store.modelFor(modelName);
        acc[pluralize(model.modelName)] = model.fieldsFor('environment').join(',');
        return acc;
      }, {});

      fields.environments += `,${relationships.join(',')}`;

      environment = this.store.queryRecord('environment', {
        include: environmentRelationships.join(','),
        fields,
        redirect: this.redirectTo,
      });
    }
    this.set('redirectTo', null);
    return environment;
  }

  async beforeModel(transition) {
    await this.session.setup();

    // Initialize intl service with default locale early to prevent FastBoot errors
    // After practice data is loaded, localeManager will set the correct locale
    this.localeManager.setLocale('en-us', 'early_init');

    let {
      channel,
      mld,
      appearance,
      redirect,
      trackingSource,
      linkSource,
      addMessage,
      reorderedNav,
      addReasonsForVisit,
      addReasonsForVisitFlag,
      token,
      anonymousId,
      spref: clinicianReferral,
      universalLink,
      isPrescreenerPreview,
      referralChannel,
      marketingClickIds,
      therapyfinderFlow,
    } = transition.to.queryParams;
    let colorSetId, theme;

    try {
      ({ colorSetId, theme } = JSON.parse(appearance));
    } catch (_err) {
      // noop
    }

    if (
      this._isPaymentLinkRoute(transition) &&
      this.currentPractice.get('featurePaymentLinksSms')
    ) {
      return;
    }

    if (redirect) {
      let queryStringParams = queryString.parse(redirect.split('?')[1]);
      channel = queryStringParams.channel;
    }

    if (universalLink) {
      this.router.transitionTo('client-app', { queryParams: {} });
      return;
    }

    if (token) await this.widgetToken.create(token);

    this.session.setProperties({
      channel,
      mld,
      colorSetId,
      theme,
      trackingSource,
      linkSource,
      addMessage,
      reorderedNav,
      addReasonsForVisit,
      addReasonsForVisitFlag,
      anonymousId,
      clinicianReferral,
      referralChannel,
      marketingClickIds,
      therapyfinderFlow,
    });

    if (isPrescreenerPreview) {
      this.prescreener.setPreviewConfig(isPrescreenerPreview);
    }

    if (redirect) {
      this.session.set('data.redirect', redirect);
      this.set('redirectTo', redirect);

      if (redirect.includes('documents')) {
        this.session.set('isHeaderFooterHidden', true);
      }
    }

    super.beforeModel(...arguments);
  }

  async afterModel(environment, transition) {
    super.afterModel(...arguments);

    if (!environment) {
      return;
    }

    let { currentPractice } = environment;
    if (!currentPractice) {
      return;
    }

    this.clientTimezone.practiceTimezone = currentPractice.timezone;

    this.currentPractice.init(currentPractice);
    this.mixpanel.initClient(currentPractice.mixpanelKey);
    this.#setupStripe(currentPractice);

    // Initialize locale after practice data is available
    await this.localeManager.initializeLocale();

    if (
      this._isPaymentLinkRoute(transition) &&
      this.currentPractice.get('featurePaymentLinksSms')
    ) {
      return;
    }

    this.session.authenticate('authenticator:environment', environment);
    if (!this.session.data.sessionId) {
      this.session.set('data.sessionId', uuidv4());
    }

    if (window._bugsnagClient && this.session.currentClientAccess) {
      window._bugsnagClient.setUser(this.session.currentClientAccess.id);
    }

    if (this.session.currentClient) {
      this.analytics.identify(this.session.currentClient);
    }

    if (this.session.anonymousId) {
      this.analytics.setAnonymousId(this.session.anonymousId);
    }

    this.#setCanonicalLink();
  }

  setupController(controller, model) {
    super.setupController(...arguments);

    if (model) {
      controller.set('messagingEmbed', htmlSafe(model.messagingEmbed));
    }
  }

  #addCanonicalLinkListener() {
    this.onRouteWillChange = this.#setCanonicalLink.bind(this);
    this.router.on('routeWillChange', this.onRouteWillChange);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.router.off('routeWillChange', this.onRouteWillChange);
  }

  #setCanonicalLink(transition) {
    let { fastboot, router } = this;
    let canonicalUrl = getCanonicalUrl({
      transition,
      fastboot,
      router,
      window,
    });
    if (canonicalUrl) {
      set(this.headData, 'canonicalUrl', canonicalUrl);
    }
  }

  @action
  error(err, transition) {
    return callbackError(err, ({ status }) => {
      console.error(err); // eslint-disable-line no-console
      window._bugsnagClient && window._bugsnagClient.notify(err);

      if (status === '401') {
        // PRIVATE: stolen from how error substates are handled
        // This is to make fastboot return 3xx instead of 500
        // https://github.com/emberjs/ember.js/blob/v3.28.0/packages/%40ember/-internals/routing/lib/system/router.ts#L1193
        this._router._markErrorAsHandled(err);
        transition.abort();

        if (this.session._isSignInPath && this.isFastBoot) {
          return;
        }

        if (this.session._isSignInPath && !transition._attemptedRetry) {
          transition._attemptedRetry = true;
          transition.retry();
          return;
        }

        this.session.signOut();
        return;
      }

      // Unknown and API error
      if (!status || status === '502') {
        status = '500';
      }

      // Bare host request
      if (status === '302') {
        status = '404';
        // used in application error template
        err.errors[0].status = '404';
      }

      if (this.isFastBoot) {
        this.set('fastboot.response.statusCode', status);
      }

      // bubble to render substate error template
      return true;
    });
  }

  getProtocol() {
    if (this.fastboot?.isFastBoot) {
      let proto = this.fastboot.request?.headers.get('x-forwarded-proto');
      return proto ? `${proto}:` : this.#testingProtocol;
    }

    return this.#testingProtocol || window?.location?.protocol;
  }

  get #testingProtocol() {
    return isEmberTesting() || ENV.railsEnv === 'test' ? 'https:' : null;
  }

  get #isSecure() {
    return this.getProtocol() === 'https:';
  }

  #setupStripe(currentPractice) {
    if (this.#isSecure) {
      this.stripe.setup(currentPractice?.customStripePublishableKey);
    }
  }

  @action
  refreshEnvironment() {
    this.store.unloadRecord(this.currentModel);
    return this.refresh();
  }

  _isPaymentLinkRoute(transition) {
    let routeName = transition?.to?.name || this.router.currentRouteName;
    return routeName?.startsWith('payment-link');
  }
}
